import { MissionConfig } from "./types";
import { BuildingState, SimulationState, LiftStatus, VisitorState, LiftCallDirection, LiftCallDirectionData, LiftCallFloorData } from "./types/simulationState";

export interface FutureVisitor {
    visitorId: string;
    weight: number;
    startingFloor: number;
    targetFloor: number;
}

/**
 * Simple Linear Congruential Generator for seeded randomness
 */
class SealedRandom {
    private seed: number;

    constructor(seed: number) {
        this.seed = seed;
    }

    // Returns a random number between 0 and 1
    next(): number {
        this.seed = (this.seed * 9301 + 49297) % 233280;
        return this.seed / 233280;
    }

    // Returns a random integer between min and max (inclusive)
    nextInt(min: number, max: number): number {
        return Math.floor(this.next() * (max - min + 1)) + min;
    }
}

export function generateVisitorTimeline(missionConfig: MissionConfig): Record<number, FutureVisitor[]> {
    const timeline: Record<number, FutureVisitor[]> = {};
    const seed = missionConfig.seedId || Date.now();
    const rng = new SealedRandom(seed);

    const numberOfVisitors = missionConfig.maxVisitors || 50;
    const maxGeneration = 120; // 2 minutes
    const distribution = missionConfig.visitorDistribution || 'Random';

    const visitors: FutureVisitor[] = [];
    for (let i = 0; i < numberOfVisitors; i++) {
        const floors = missionConfig.floors.length;
        const startingFloor = rng.nextInt(0, floors - 1);
        let targetFloor = rng.nextInt(0, floors - 1);
        while (targetFloor === startingFloor) {
            targetFloor = rng.nextInt(0, floors - 1);
        }

        const visitorMaxWeight = Object.values(missionConfig.lifts).reduce((max, lift) => {
            if (lift.bottomFloor <= startingFloor && lift.topFloor >= startingFloor) {
                return Math.max(max, lift.weightLimit);
            }
            return max;
        }, 0);
        const effectiveMaxWeight = visitorMaxWeight > 0 ? visitorMaxWeight : 100;

        visitors.push({
            visitorId: `v_${i}`,
            weight: rng.nextInt(Math.floor(effectiveMaxWeight * 0.1), effectiveMaxWeight),
            startingFloor: startingFloor,
            targetFloor: targetFloor
        });
    }

    if (distribution === 'Interval') {
        const interval = Math.max(1, Math.floor(maxGeneration / numberOfVisitors));
        visitors.forEach((visitor, index) => {
            const gen = Math.min(maxGeneration, 1 + index * interval);
            if (!timeline[gen]) timeline[gen] = [];
            timeline[gen]!.push(visitor);
        });
    } else if (distribution === 'Burst') {
        const burstSize = 5;
        const numberOfBursts = Math.ceil(numberOfVisitors / burstSize);
        for (let i = 0; i < numberOfBursts; i++) {
            const burstGen = rng.nextInt(1, maxGeneration);
            if (!timeline[burstGen]) timeline[burstGen] = [];
            for (let j = 0; j < burstSize; j++) {
                const visitorIndex = i * burstSize + j;
                const visitor = visitors[visitorIndex];
                if (visitor) {
                    timeline[burstGen]!.push(visitor);
                }
            }
        }
    } else if (distribution === 'Sine') {
        visitors.forEach((visitor) => {
            // Distribute visitors using a sine wave probability density
            // Higher probability in middle (peak)
            let gen = 0;
            let found = false;
            while (!found) {
                const candidateGen = rng.nextInt(1, maxGeneration);
                const normalized = (candidateGen / maxGeneration) * Math.PI;
                const prob = Math.sin(normalized);
                if (rng.next() < prob) {
                    gen = candidateGen;
                    found = true;
                }
            }
            if (!timeline[gen]) timeline[gen] = [];
            timeline[gen]!.push(visitor);
        });
    } else {
        // Default: Random (Uniform)
        visitors.forEach(visitor => {
            const gen = rng.nextInt(1, maxGeneration);
            if (!timeline[gen]) timeline[gen] = [];
            timeline[gen]!.push(visitor);
        });
    }

    return timeline;
}

export function processVisitorTimeline(simulation: SimulationState, timeline: Record<number, FutureVisitor[]>): void {
    const currentGeneration = simulation.generation;
    const newVisitors = timeline[currentGeneration];

    if (newVisitors && newVisitors.length > 0) {
        newVisitors.forEach(futureVisitor => {
            // Add to global visitors list
            const visitorState: VisitorState = {
                currentLift: null,
                targetFloor: futureVisitor.targetFloor,
                weight: futureVisitor.weight,
                generationEnter: currentGeneration
            };
            simulation.building.visitors[futureVisitor.visitorId] = visitorState;

            // Add to floor queue
            const floor = simulation.building.floors[futureVisitor.startingFloor];
            if (floor) {
                floor.visitors.push(futureVisitor.visitorId);
            }
        });
    }
}

export function newSimulation(missionConfig: MissionConfig): SimulationState {
    const building: BuildingState = {
        visitors: {},
        lifts: {},
        floors: {},
    };

    for (const [liftId, liftConfig] of Object.entries(missionConfig.lifts)) {
        building.lifts[liftId] = {
            currentFloor: liftConfig.startingFloor,
            targetedFloors: [],
            status: LiftStatus.IDLE,
            passengers: [],
            requests: [],
            config: liftConfig,
        };
    }

    missionConfig.floors.forEach((floorConfig, floorNumber) => {
        building.floors[floorNumber] = {
            visitors: [],
            requests: [],
            config: floorConfig,
        };
    });

    return {
        generation: 0,
        building: building,
        completedTrips: [],
    }
}

export function onLiftMoveCompleted(simulation: SimulationState, liftId: string, newFloorNumber: number): void {
    const liftState = simulation.building.lifts[liftId];
    if (!liftState) return;
    liftState.currentFloor = newFloorNumber;
}

export function setLiftTargets(simulation: SimulationState, liftId: string, targetFloorNumbers: number[]): void {
    const liftState = simulation.building.lifts[liftId];
    if (!liftState) return;
    liftState.targetedFloors = targetFloorNumbers;
}

export function updateLiftStatus(simulation: SimulationState,
    onLiftMoveStarted: (liftId: string, currentFloorNumber: number, targetFloorNumber: number) => void,
    onLiftMoveCompleted: (liftId: string, newFloorNumber: number) => void): void {
    const building = simulation.building;
    for (const [liftId, lift] of Object.entries(building.lifts)) {
        switch (lift.status) {
            case LiftStatus.IDLE:
                if (lift.targetedFloors.length > 0) {
                    lift.status = LiftStatus.MOVING;
                    onLiftMoveStarted(liftId, lift.currentFloor, lift.targetedFloors[0]!);
                }
                break;
            case LiftStatus.MOVING:
                if (lift.targetedFloors.length === 0) {
                    lift.status = LiftStatus.IDLE;
                } else if (lift.targetedFloors[0] === lift.currentFloor) {
                    // Reached target, idle for a generation.
                    lift.status = LiftStatus.IDLE;
                    lift.targetedFloors.shift();
                }
                if (lift.status === LiftStatus.IDLE) {
                    onLiftMoveCompleted(liftId, lift.currentFloor);
                }
                break;
        }
    }
}

export function processVisitors(simulation: SimulationState): void {
    const building = simulation.building;

    // 1 & 2: Unload and Load logic for each lift
    for (const [liftId, lift] of Object.entries(building.lifts)) {
        if (lift.status === LiftStatus.IDLE) {
            // --- 1. UNLOAD ---
            const passengersToUnload: string[] = [];
            for (const visitorId of lift.passengers) {
                const visitor = building.visitors[visitorId];
                if (visitor && visitor.targetFloor === lift.currentFloor) {
                    passengersToUnload.push(visitorId);
                }
            }

            for (const visitorId of passengersToUnload) {
                // Remove from lift passengers
                lift.passengers = lift.passengers.filter(id => id !== visitorId);

                const visitor = building.visitors[visitorId];
                if (!visitor) continue;

                // Store trip record
                simulation.completedTrips.push({
                    generationEnter: visitor.generationEnter,
                    generationLeave: simulation.generation
                });
                // Remove from global visitors
                delete building.visitors[visitorId];
            }

            // Cleanup lift requests for current floor
            lift.requests = lift.requests.filter(req => {
                if (req.requestType === 'floor') {
                    const data = req.requestData as LiftCallFloorData;
                    return data.requestedFloor !== lift.currentFloor;
                }
                return true;
            });

            // --- 2. LOAD ---
            if (lift.targetedFloors.length > 0) {
                const targetFloor = lift.targetedFloors[0]!;
                const liftDirection = targetFloor > lift.currentFloor ? LiftCallDirection.UP : LiftCallDirection.DOWN;
                const floorToLoadFrom = building.floors[lift.currentFloor];

                if (floorToLoadFrom && floorToLoadFrom.visitors.length > 0) {
                    const remainingFloorVisitors: string[] = [];

                    // Calculate current weight
                    let currentWeight = lift.passengers.reduce((sum, vid) => sum + (building.visitors[vid]?.weight || 0), 0);

                    const liftWeightLimit = lift.config.weightLimit;

                    for (const visitorId of floorToLoadFrom.visitors) {
                        const visitor = building.visitors[visitorId];
                        if (!visitor) continue;

                        const visitorDirection = visitor.targetFloor > lift.currentFloor ? LiftCallDirection.UP : LiftCallDirection.DOWN;

                        if (visitorDirection === liftDirection) {
                            if (currentWeight + visitor.weight <= liftWeightLimit) {
                                lift.passengers.push(visitorId);
                                visitor.currentLift = liftId;
                                currentWeight += visitor.weight;
                            } else {
                                remainingFloorVisitors.push(visitorId);
                            }
                        } else {
                            remainingFloorVisitors.push(visitorId);
                        }
                    }
                    floorToLoadFrom.visitors = remainingFloorVisitors;
                }

                // Floor Requests Cleanup
                const floorToCleanup = building.floors[lift.currentFloor];
                if (floorToCleanup) {
                    floorToCleanup.requests = floorToCleanup.requests.filter(req => {
                        if (req.requestType === 'single') return false;
                        if (req.requestType === 'direction') {
                            const data = req.requestData as LiftCallDirectionData;
                            return data.requestedDirection !== liftDirection;
                        }
                        if (req.requestType === 'floor') {
                            const data = req.requestData as LiftCallFloorData;
                            const reqDir = data.requestedFloor > lift.currentFloor ? LiftCallDirection.UP : LiftCallDirection.DOWN;
                            return reqDir !== liftDirection;
                        }
                        return true;
                    });
                }
            }
        }
    }

    // 3: Visitors Requests Sync
    // Sync Floor Requests
    const floorsCount = Object.keys(simulation.building.floors).length;
    for (let floorNumber = 0; floorNumber < floorsCount; floorNumber++) {
        const floor = building.floors[floorNumber];
        if (!floor) continue;

        const buttonType = floor.config.buttonType;

        for (const visitorId of floor.visitors) {
            const visitor = building.visitors[visitorId];
            if (!visitor) continue;

            const visitorDirection = visitor.targetFloor > floorNumber ? LiftCallDirection.UP : LiftCallDirection.DOWN;

            let requestExists = false;
            if (buttonType === 'SingleCall') {
                requestExists = floor.requests.some(req => req.requestType === 'single');
                if (!requestExists) {
                    floor.requests.push({
                        requestId: crypto.randomUUID(),
                        requestType: 'single',
                        requestData: null
                    });
                }
            } else if (buttonType === 'UpDown') {
                requestExists = floor.requests.some(req =>
                    req.requestType === 'direction' &&
                    (req.requestData as LiftCallDirectionData).requestedDirection === visitorDirection
                );
                if (!requestExists) {
                    floor.requests.push({
                        requestId: crypto.randomUUID(),
                        requestType: 'direction',
                        requestData: { requestedDirection: visitorDirection }
                    });
                }
            } else if (buttonType === 'NumberedFloors') {
                requestExists = floor.requests.some(req =>
                    req.requestType === 'floor' &&
                    (req.requestData as LiftCallFloorData).requestedFloor === visitor.targetFloor
                );
                if (!requestExists) {
                    floor.requests.push({
                        requestId: crypto.randomUUID(),
                        requestType: 'floor',
                        requestData: { requestedFloor: visitor.targetFloor }
                    });
                }
            }
        }
    }

    // Sync Lift Requests
    for (const lift of Object.values(building.lifts)) {
        const currentFloor = lift.currentFloor;

        for (const visitorId of lift.passengers) {
            const visitor = building.visitors[visitorId];
            if (!visitor) continue;

            const visitorDirection = visitor.targetFloor > currentFloor ? LiftCallDirection.UP : LiftCallDirection.DOWN;

            const targetFloor = lift.targetedFloors[0];
            if (targetFloor === undefined ||
                (targetFloor > currentFloor && visitorDirection === LiftCallDirection.UP) ||
                (targetFloor < currentFloor && visitorDirection === LiftCallDirection.DOWN)) {
                const requestExists = lift.requests.some(req =>
                    req.requestType === 'floor' &&
                    (req.requestData as LiftCallFloorData).requestedFloor === visitor.targetFloor
                );
                if (!requestExists) {
                    lift.requests.push({
                        requestId: crypto.randomUUID(),
                        requestType: 'floor',
                        requestData: { requestedFloor: visitor.targetFloor }
                    });
                }
            }
        }
    }
}